import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import CustomersPage from "./pages/CustomersPage";
import CustomerStatementPage from "./pages/CustomerStatementPage";
import OrdersPage from "./pages/OrdersPage";
import DeliverySheetPage from "./pages/DeliverySheetPage";
import ReportsPage from "./pages/ReportsPage";
import DriversPage from "./pages/DriversPage";
import DriverViewPage from "./pages/DriverViewPage";
import ExpensesPage from "./pages/ExpensesPage";
import FinancialsPage from "./pages/FinancialsPage";
import ProductsPage from "./pages/ProductsPage";
import SettingsPage from "./pages/SettingsPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import DeliveryProofsPage from "./pages/DeliveryProofsPage";
import CustomerPortalPage from "./pages/CustomerPortalPage";
import ProductionPage from "./pages/ProductionPage";
import ProductionReportPage from "./pages/ProductionReportPage";
import InvoicesPage from "./pages/InvoicesPage";
import GermanInvoicesPage from "./pages/GermanInvoicesPage";
import InvoicePrintPage from "./pages/InvoicePrintPage";
import ExportAllStatementsPage from "./pages/ExportAllStatementsPage";
import FinancialDuesPage from "./pages/FinancialDuesPage";
import ProfitReportPage from "./pages/ProfitReportPage";
import EmployeesPage from "./pages/EmployeesPage";
import WithdrawalsPage from "./pages/WithdrawalsPage";
import StaffUsersPage from "./pages/StaffUsersPage";
import BakeryRegistrationPage from "./pages/BakeryRegistrationPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import WhatsAppMessagesPage from "./pages/WhatsAppMessagesPage";
import AttendanceReportPage from "./pages/AttendanceReportPage";
import MyShiftPage from "./pages/MyShiftPage";
import WhatsAppConnectPage from "./pages/WhatsAppConnectPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import Layout from "./components/Layout";
import "./App.css";

// Protected Route Component for Admin
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin, isStaff, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Allow both admin and staff users
  if (!isAdmin && !isStaff) {
    return <Navigate to="/driver" replace />;
  }
  
  // Check if staff has ONLY attendance permission - redirect to my-shift
  if (isStaff && user?.permissions) {
    const p = user.permissions;
    const isShiftOnly = p.view_attendance && 
      !p.view_production && !p.view_customers && !p.view_orders && 
      !p.view_delivery && !p.view_expenses && !p.view_invoices && !p.view_profit_report;
    
    if (isShiftOnly && window.location.pathname !== '/my-shift') {
      return <Navigate to="/my-shift" replace />;
    }
  }
  
  return children;
};

// Protected Route for Driver
const DriverRoute = ({ children }) => {
  const { isAuthenticated, loading, isDriver } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isDriver) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Protected Route for Super Admin Only
const SuperAdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!user?.is_super_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Route based on role
const RoleBasedRedirect = () => {
  const { isAdmin, isDriver, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }
  
  if (isDriver) {
    return <Navigate to="/driver" replace />;
  }
  
  if (isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            
            {/* Super Admin Route - Separate URL */}
            <Route
              path="/super-admin"
              element={
                <SuperAdminRoute>
                  <SuperAdminPage />
                </SuperAdminRoute>
              }
            />
            
            {/* Driver Route */}
            <Route
              path="/driver"
              element={
                <DriverRoute>
                  <DriverViewPage />
                </DriverRoute>
              }
            />
            
            {/* Invoice Print Route - Outside Layout for clean A4 printing */}
            <Route
              path="/invoice-print/:invoiceId"
              element={
                <AdminRoute>
                  <InvoicePrintPage />
                </AdminRoute>
              }
            />
            
            {/* Export All Statements Route - Outside Layout for clean printing */}
            <Route
              path="/export-statements"
              element={
                <AdminRoute>
                  <ExportAllStatementsPage />
                </AdminRoute>
              }
            />
            
            {/* Admin Routes */}
            <Route
              path="/"
              element={
                <AdminRoute>
                  <Layout />
                </AdminRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="production" element={<ProductionPage />} />
              <Route path="production-report" element={<ProductionReportPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="german-invoices" element={<GermanInvoicesPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customer-statement/:customerId" element={<CustomerStatementPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="delivery" element={<DeliverySheetPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="expenses" element={<ExpensesPage />} />
              <Route path="financial-dues" element={<FinancialDuesPage />} />
              <Route path="financials" element={<FinancialsPage />} />
              <Route path="profit-report" element={<ProfitReportPage />} />
              <Route path="employees" element={<EmployeesPage />} />
              <Route path="withdrawals" element={<WithdrawalsPage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="drivers" element={<DriversPage />} />
              <Route path="delivery-proofs" element={<DeliveryProofsPage />} />
              <Route path="staff-users" element={<StaffUsersPage />} />
              <Route path="audit-logs" element={<AuditLogsPage />} />
              <Route path="whatsapp-messages" element={<WhatsAppMessagesPage />} />
              <Route path="whatsapp-connect" element={<WhatsAppConnectPage />} />
              <Route path="attendance-report" element={<AttendanceReportPage />} />
              <Route path="my-shift" element={<MyShiftPage />} />
              <Route path="announcements" element={<AnnouncementsPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            
            {/* Customer Portal - Public Route */}
            <Route path="/portal" element={<CustomerPortalPage />} />
            
            {/* Bakery Registration - Public Route */}
            <Route path="/register-bakery" element={<BakeryRegistrationPage />} />
            
            <Route path="*" element={<RoleBasedRedirect />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
